import { Link, useParams } from 'react-router';

import type { ApiError } from '../../api/client';
import { useVenue } from '../../api/venues';
import { EmptyState, ErrorState, Spinner } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { describeFailure, venueCardPath } from '../venues/venues-model';
import { prefillAccessCopy, venuePrefillAccess } from './cabinet-model';
import { PrefillOnlineBlock } from './PrefillOnlineBlock';
import './cabinet.css';

/**
 * «Онлайн-набор» of ONE venue (T.Z27) — the venue card's «Онлайн-набор» section as a
 * page, rendering the SAME block the curator cabinet renders (`PrefillOnlineBlock`)
 * with `venueId` pinned and no venue picker, no offline roster and no «Все места».
 *
 * WHO IT IS FOR (owner model, 2026-09-13): «Куратор площадки» is an external-only
 * setting — a МЕНЕДЖЕР never becomes one and never sees `/cabinet`. But a МЕНЕДЖЕР
 * assigned to a flagged ONLINE venue manages that venue's set exactly like a curator:
 * add children (ФИО rule included), edit and remove own unmatched rows, export. The
 * api already authorises all of that — `#26.2–#26.6` admit any session whose user
 * holds a `venue_managers`/`venue_curators` link to a flagged venue (and an ADMIN
 * over its own link union), while `#26`/`#26.1` stay a КУРАТОР + АДМИН surface, so
 * this page calls nothing from those two.
 *
 * THE GUARD IS THE SERVER's; this page only refuses to fire requests it knows are
 * refused. `venuePrefillAccess` re-reads the api's own published scope
 * (`user.cabinetVenueIds` on `/auth/me`) rather than recomputing the link rule, and
 * every refusal renders as a named state — never as an empty table that reads as
 * «нет детей».
 */
export function VenuePrefillPage() {
  const params = useParams();
  const { role, cabinetVenueIds } = useAuth();
  const venueId = Number.parseInt(params.venueId ?? '', 10);

  if (!Number.isFinite(venueId) || venueId <= 0) {
    return (
      <div className="card">
        <EmptyState title="Площадка не найдена" hint="В адресе нет номера площадки — откройте её из списка площадок." />
      </div>
    );
  }

  return <VenuePrefillBody venueId={venueId} role={role} cabinetVenueIds={cabinetVenueIds} />;
}

function VenuePrefillBody({
  venueId,
  role,
  cabinetVenueIds,
}: {
  venueId: number;
  role: ReturnType<typeof useAuth>['role'];
  cabinetVenueIds: ReturnType<typeof useAuth>['cabinetVenueIds'];
}) {
  const query = useVenue(venueId);
  const access = venuePrefillAccess({
    venueId,
    // The flag is only known once #7 has answered; until then the page cannot
    // decide, so an unresolved session reads as a pending page, not as a refusal.
    allowCuratorChildren: query.data?.allowCuratorChildren ?? false,
    role,
    cabinetVenueIds,
  });

  if (query.isPending) {
    return (
      <div className="page-header">
        <h1>Онлайн-набор</h1>
        <Spinner label="Загружаем площадку" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <>
        <PageHeader venueId={venueId} venueName={null} />
        <div className="card">
          <ErrorState
            message={describeFailure(query.error as ApiError | null, 'Не удалось загрузить карточку площадки.')}
            onRetry={() => void query.refetch()}
          />
        </div>
      </>
    );
  }

  if (access !== 'open') {
    const copy = prefillAccessCopy(access);
    return (
      <>
        <PageHeader venueId={venueId} venueName={query.data.name} />
        <div className="card" data-testid={`venue-prefill-${access}`}>
          <EmptyState title={copy.title} hint={copy.hint} />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader venueId={venueId} venueName={query.data.name} />
      <PrefillOnlineBlock
        venueId={venueId}
        venueName={query.data.name}
        canAdd
        heading="Онлайн-набор площадки"
        // v1.9.26 (T.Z125) — the same two-population wording the cabinet's own block
        // uses: the manager's rows are editable until the match, the venue's ONLINE-set
        // children (the widened #26.2 rows) are read-only, the group is their one fact.
        sub={`${query.data.name} · менеджер ведёт набор как куратор: свою строку можно открыть кликом и править, пока ребёнок не сопоставлен; строки платформы — только чтение (группу назначить можно).`}
      />
    </>
  );
}

function PageHeader({ venueId, venueName }: { venueId: number; venueName: string | null }) {
  return (
    <div className="page-header">
      <div>
        <h1>Онлайн-набор</h1>
        <p className="page-header__sub">
          {venueName === null ? 'Площадка' : venueName} · строки вносятся вручную и сопоставляются автоматически
        </p>
        <p className="venue-head__back">
          <Link to={venueCardPath(venueId, { page: 1 })} data-testid="venue-prefill-back">
            ← К карточке площадки
          </Link>
        </p>
      </div>
    </div>
  );
}
